import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Appointment } from '../types/supabase';
import ReCAPTCHA from "react-google-recaptcha";
import DOMPurify from 'dompurify';

interface Class {
  id: string;
  class_name: string;
  start_time: string;
  end_time: string | null;
  days: string[];
  type: string;
  repeats_weekly: boolean | null;
  age_range: string | null;
}

interface Lead {
  id: string;
  name: string;
  phone: string;
  studio_id: string;
}

interface Studio {
  id: string;
  name: string;
  address: string;
  phone: string;
  email: string;
  closure_dates?: string[] | null;
}

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  name: 100,
  email: 254,
  phone: 20,
};

const SUBMISSION_COOLDOWN = 2000; // 2 seconds between submissions

// Input sanitization function
const sanitizeInput = (input: string, fieldType?: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // For text fields (names), preserve spaces and only remove dangerous content
  if (fieldType === 'name') {
    // Only remove dangerous patterns, preserve ALL spaces (including trailing)
    sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
    sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
    sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
    sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
    sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
    
    // DON'T trim for text fields - preserve all spaces including trailing ones
    // Users should be able to type "John " and keep the space
    
  } else {
    // For other fields, use more aggressive sanitization
    sanitized = sanitized.trim();
    sanitized = sanitized.replace(/[<>]/g, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/data:text\/html/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  }
  
  return sanitized;
};

// Enhanced validation function
const validateInput = (value: string, type: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  
  const sanitized = sanitizeInput(value, type);
  if (sanitized.length === 0) return false;

  // Check length limits
  if (MAX_LENGTHS[type] && sanitized.length > MAX_LENGTHS[type]) {
    return false;
  }
  
  // Type-specific validation
  switch (type) {
    case 'email':
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(sanitized);
    case 'phone':
      const phoneRegex = /^[\d\s\-\(\)\.]+$/;
      return phoneRegex.test(sanitized) && sanitized.replace(/\D/g, '').length >= 10;
    case 'name':
      // Allow letters, spaces, hyphens, apostrophes, periods for names
      // Check for dangerous patterns instead of restrictive allowed characters
      const nameDangerousPatterns = /[<>]|javascript:|vbscript:|on\w+\s*=/i;
      return !nameDangerousPatterns.test(sanitized) && sanitized.length >= 1;
    default:
      return sanitized.length > 0;
  }
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  console.warn(`Security Event: ${event}`, {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    details
  });
};

// Utility function for consistent input styling
const getInputClassName = (baseClasses: string) => {
  return `${baseClasses} bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100`;
};

export default function PublicAppointmentBooking() {
  const { leadId } = useParams<{ leadId: string }>();
  const [lead, setLead] = useState<Lead | null>(null);
  const [studio, setStudio] = useState<Studio | null>(null);
  const [classes, setClasses] = useState<Class[]>([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  
  // Security-related state
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);

  useEffect(() => {
    if (leadId) {
      fetchLeadAndClasses();
      fetchCsrfToken();
    }
  }, [leadId]);

  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/csrf-token`);
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          setCsrfToken(data.token);
        } else {
          logSecurityEvent('CSRF_TOKEN_INVALID_RESPONSE', { data });
        }
      } else {
        logSecurityEvent('CSRF_TOKEN_FETCH_ERROR', { status: response.status, statusText: response.statusText });
      }
    } catch (error) {
      logSecurityEvent('CSRF_TOKEN_FETCH_FAILED', { error: error instanceof Error ? error.message : 'Unknown error' });
      // In development, generate a fallback token
      if (process.env.NODE_ENV !== 'production') {
        const fallbackToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
        setCsrfToken(fallbackToken);
        console.warn('Using fallback CSRF token in development mode');
      }
    }
  };

  const fetchLeadAndClasses = async () => {
    try {
      setLoading(true);
      
      // Fetch lead details
      const leadResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/appointments/lead/${leadId}`);
      if (!leadResponse.ok) {
        throw new Error('Lead not found');
      }
      
      const leadData = await leadResponse.json();
      setLead(leadData);
      setFormData(prev => ({
        ...prev,
        name: leadData.name || '',
        email: leadData.email || '',
        phone: leadData.phone || ''
      }));

      // Fetch studio details
      const studioResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/appointments/studio/${leadData.studio_id}`);
      if (studioResponse.ok) {
        const studioData = await studioResponse.json();
        setStudio(studioData);
      }

      // Fetch available classes
      const classesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/appointments/classes/${leadData.studio_id}`);
      if (classesResponse.ok) {
        const classesData = await classesResponse.json();
        setClasses(classesData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load appointment booking page. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    let newValue = value;

    // Sanitize input first
    if (typeof value === 'string') {
      newValue = sanitizeInput(value, field);
    }

    if (field === "phone") {
      // Remove all non-digit characters
      const digits = newValue.replace(/\D/g, "");
      // Format as XXX-XXX-XXXX
      if (digits.length <= 3) {
        newValue = digits;
      } else if (digits.length <= 6) {
        newValue = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else if (digits.length <= 10) {
        newValue = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      } else {
        newValue = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
      }
    }

    // Validate input length
    if (MAX_LENGTHS[field] && newValue.length > MAX_LENGTHS[field]) {
      newValue = newValue.substring(0, MAX_LENGTHS[field]);
    }

    setFormData(prev => ({ ...prev, [field]: newValue }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedClass || !selectedDate) {
      setError('Please select both a class and date.');
      return;
    }

    // Validate form inputs
    if (!validateInput(formData.name, 'name')) {
      setError('Please enter a valid name.');
      return;
    }

    if (!validateInput(formData.email, 'email')) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!validateInput(formData.phone, 'phone')) {
      setError('Please enter a valid phone number.');
      return;
    }

    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setError('Please wait before submitting again');
      return;
    }

    // CSRF token validation (strict in production, flexible in development)
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { leadId });
      if (process.env.NODE_ENV === 'production') {
        setError('Security token missing. Please refresh the page and try again.');
        return;
      } else {
        // In development, log the issue but allow submission to continue
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    // ReCaptcha validation (always required)
    if (!recaptchaValue && process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
      setError('Please complete the reCAPTCHA verification');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setLastSubmissionTime(now);

      const selectedClassData = classes.find(c => c.id === selectedClass);
      if (!selectedClassData) {
        throw new Error('Selected class not found');
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/appointments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          lead_id: leadId,
          class_id: selectedClass,
          appointment_date: selectedDate,
          appointment_time: selectedClassData.start_time,
          status: 'scheduled',
          notes: `Appointment booked via public booking page. Contact info: ${formData.email}`,
          recaptchaToken: recaptchaValue,
          csrfToken: csrfToken
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to schedule appointment');
      }

      setSuccess(true);
      
      // Reset reCAPTCHA after successful submission
      setRecaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (error) {
      logSecurityEvent('FORM_SUBMISSION_ERROR', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        leadId 
      });
      setError(error instanceof Error ? error.message : 'Failed to schedule appointment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Get available dates for the next 2 weeks
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      // Use local date components to avoid timezone conversion issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
    }
    
    return dates;
  };

  // Get available dates for a specific class
  const getAvailableDatesForClass = (classId: string) => {
    if (!classId) return [];
    
    const selectedClass = classes.find(c => c.id === classId);
    if (!selectedClass) return [];
    
    const availableDates = [];
    const allDates = getAvailableDates();
    
    for (const date of allDates) {
      // Parse date string (YYYY-MM-DD) and create Date object in local timezone
      const [year, month, day] = date.split('-').map(Number);
      const dateObj = new Date(year, month - 1, day);
      const dayOfWeek = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
      const isClosed = (studio?.closure_dates || []).includes(date);
      if (selectedClass.days.includes(dayOfWeek) && !isClosed) {
        availableDates.push(date);
      }
    }
    
    return availableDates;
  };

  // Filter classes by selected date
  const getClassesForDate = (date: string) => {
    if (!date) return [];
    
    // Parse date string (YYYY-MM-DD) and create Date object in local timezone
    const [year, month, day] = date.split('-').map(Number);
    const dateObj = new Date(year, month - 1, day);
    const dayOfWeek = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
    const filteredClasses = classes.filter(cls => {
      // Check if the class has the selected day in its days array
      return cls.days && cls.days.includes(dayOfWeek);
    });
    
    
    return filteredClasses;
  };

  // Format time for display
  const formatTime = (time: string) => {
    try {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const minute = parseInt(minutes);
      
      const period = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      
      return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch (error) {
      return time;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading appointment booking...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 text-center">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Appointment Scheduled!</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Thank you for scheduling your appointment. We'll see you soon!
          </p>
          {studio && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              <p><strong>{studio.name}</strong></p>
              <p>{studio.address}</p>
              <p>{studio.phone}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        {studio && (
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{studio.name}</h1>
            <p className="text-gray-600 dark:text-gray-400">Schedule Your Class</p>
          </div>
        )}
        
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <p className="text-red-800 dark:text-red-300 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
            <input
              type="text"
              value={DOMPurify.sanitize(formData.name, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={getInputClassName("w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500")}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
            <input
              type="email"
              value={DOMPurify.sanitize(formData.email, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={getInputClassName("w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500")}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
            <input
              type="tel"
              value={DOMPurify.sanitize(formData.phone, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className={getInputClassName("w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500")}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select Class</label>
            <select
              value={selectedClass}
              onChange={(e) => {
                setSelectedClass(e.target.value);
                setSelectedDate(''); // Reset date selection when class changes
              }}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            >
              <option value="">Choose a class</option>
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>
                  {cls.class_name} ({formatTime(cls.start_time)} - {cls.end_time ? formatTime(cls.end_time) : 'TBD'}){cls.age_range ? ` - Ages: ${cls.age_range}` : ''}
                </option>
              ))}
            </select>
            {classes.length === 0 && (
              <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                <p>No classes available</p>
              </div>
            )}
          </div>

          {selectedClass && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select Date</label>
              <select
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              >
                <option value="">Choose a date</option>
                {getAvailableDatesForClass(selectedClass).map(date => {
                  // Parse date string (YYYY-MM-DD) and create Date object in local timezone
                  const [year, month, day] = date.split('-').map(Number);
                  const dateObj = new Date(year, month - 1, day);
                  return (
                    <option key={date} value={date}>
                      {dateObj.toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </option>
                  );
                })}
              </select>
              {getAvailableDatesForClass(selectedClass).length === 0 && (
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  <p>No available dates for this class</p>
                </div>
              )}
            </div>
          )}

          {/* ReCaptcha */}
          {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
            <div className="flex justify-center">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={setRecaptchaValue}
                onExpired={() => setRecaptchaValue(null)}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !selectedClass || !selectedDate}
            className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            {submitting ? 'Scheduling...' : 'Schedule Appointment'}
          </button>
        </form>

        {studio && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-600 text-center text-sm text-gray-500 dark:text-gray-400">
            <p><strong>{studio.name}</strong></p>
            <p>{studio.address}</p>
            <p>{studio.phone}</p>
            <p>{studio.email}</p>
          </div>
        )}
      </div>
    </div>
  );
}
